import React from 'react';
import { View, Pressable, Platform, ScrollView } from 'react-native';
import { Text } from '@/components/ui/text';
import { useLocationPermission } from '../hooks/use-location-permission';

interface PermissionsScreenProps {
  onNext: () => Promise<void>;
  isAdvancing: boolean;
}

export function PermissionsScreen({ onNext, isAdvancing }: PermissionsScreenProps) {
  const { status: locationStatus, requestPermission } = useLocationPermission();

  const handleAllowLocation = async () => {
    await requestPermission();
  };

  return (
    <View className="flex-1 px-6 pt-4">
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
        <Text className="text-xl font-bold text-foreground mb-2">Permissions</Text>
        <Text className="text-sm text-muted-foreground mb-6">
          We need a couple of permissions to give you the best experience.
        </Text>

        <View className="mb-6">
          <Text className="text-base font-semibold text-foreground mb-1">Location</Text>
          <Text className="text-sm text-muted-foreground mb-3">
            Used for finding nearby restaurants, check-ins, and recommendations.
          </Text>

          {locationStatus === 'granted' ? (
            <View className="bg-green-100 rounded-lg py-2 px-4">
              <Text className="text-green-800 text-sm">Location access granted</Text>
            </View>
          ) : locationStatus === 'denied' ? (
            <View>
              <Text className="text-sm text-muted-foreground mb-2">
                Location helps us show restaurants near you. You can enable it later in Settings.
              </Text>
              <Pressable
                onPress={handleAllowLocation}
                accessibilityLabel="Allow Location"
                className="bg-surface-secondary rounded-lg py-2 px-4"
              >
                <Text className="text-foreground text-sm text-center">Try Again</Text>
              </Pressable>
            </View>
          ) : (
            <Pressable
              onPress={handleAllowLocation}
              accessibilityLabel="Allow Location"
              className="bg-primary rounded-lg py-2 px-4"
            >
              <Text className="text-primary-foreground text-sm text-center">Allow Location</Text>
            </Pressable>
          )}
        </View>

        {Platform.OS !== 'web' && (
          <View className="mb-6">
            <Text className="text-base font-semibold text-foreground mb-1">Notifications</Text>
            <Text className="text-sm text-muted-foreground mb-3">
              Get notified when friends check in or when you earn badges.
            </Text>
          </View>
        )}
      </ScrollView>

      <Pressable
        onPress={onNext}
        disabled={isAdvancing}
        accessibilityLabel="Continue"
        className="bg-primary rounded-lg py-3 px-8 mb-4"
        style={{ opacity: isAdvancing ? 0.5 : 1 }}
      >
        <Text className="text-primary-foreground text-base font-semibold text-center">
          Continue
        </Text>
      </Pressable>
    </View>
  );
}
